// src/components/scambi/ScambiWinToWinTab.js
// Scambi Win-To-Win, spostato fuori da una singola asta (prima era un tab
// interno ad AuctionDetailPage). Flusso guidato: (1) scegli l'asta da
// analizzare, (2) scegli i tuoi giocatori, (3) scegli CON CHI scambiare, poi
// vengono generati i pacchetti — a differenza di prima, dove i risultati
// venivano mostrati per tutte le squadre insieme.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2, Repeat, RotateCcw, ShieldAlert, Eye,
  TrendingUp, ShieldCheck, CalendarDays, Activity, Scale, Star,
  Radar, Target, Info,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  getAuctionRolesMode,
  getUserAuctions,
  getUserWatchedAuctions,
} from '../../firebase/auctionService';
import { getUserDisplayNamesByIds } from '../../firebase/usersService';
import { getAllPlayerRatingsMap } from '../../firebase/playerService';
import useAuctionRosterData from '../../hooks/useAuctionRosterData';
import AppTooltip from '../Tooltip';
import { RoleChip, ROLE_LABELS } from '../auction/auctionCommon';
import {
  buildFreeAgentIndex,
  buildGroupTradeSuggestions,
  getDistinctBuckets,
  rankTargetTeams,
} from '../auction/auctionAnalysis';
import { buildPlayerAvailability } from '../../utils/buildPlayerAvailability';
import {
  AcceptanceBadge,
  GroupSuggestionCard,
  GroupedPlayerPickList,
  PublishTradeControl,
  TradeAnalysisOverlay,
  TradeAnalysisReport,
  buildRosterCounts,
  groupPurchasesByBucket,
} from './ScambiCommon';

function ScambiWinToWinAnalysis({
  auction,
  rolesMode,
  teamNames,
  rosterByTeam,
  myRosterPurchases,
  myTeamName,
  players,
  purchases,
  isWatched,
  onChangeAuction,
  onGoToCommunity,
}) {
  const myRosterGrouped = useMemo(
    () => groupPurchasesByBucket(myRosterPurchases, rolesMode),
    [myRosterPurchases, rolesMode]
  );
  const myRosterCountsForMe = useMemo(
    () => buildRosterCounts(myRosterPurchases, rolesMode),
    [myRosterPurchases, rolesMode]
  );
  const freeAgentsByRole = useMemo(
    () => buildFreeAgentIndex({ players, purchases, rolesMode }),
    [players, purchases, rolesMode]
  );

  // Disponibilità (infortuni + probabili formazioni) di tutto il dataset:
  // serve al motore per segnalare/retrocedere i pacchetti con infortunati.
  const [availabilityById, setAvailabilityById] = useState(null);
  useEffect(() => {
    let cancelled = false;
    buildPlayerAvailability(players || [])
      .then((map) => { if (!cancelled) setAvailabilityById(map); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [players]);

  // Forma recente (voti storici) di tutta la lega: alimenta i segnali "in un
  // momento"/"in calo" e "compra basso" nei pacchetti suggeriti.
  const [ratingsById, setRatingsById] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getAllPlayerRatingsMap()
      .then((map) => { if (!cancelled) setRatingsById(map); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const otherTeamNames = useMemo(
    () => teamNames.filter((t) => t !== myTeamName),
    [teamNames, myTeamName]
  );

  const [selectedMyIds, setSelectedMyIds] = useState(() => new Set());
  const [targetTeam, setTargetTeam] = useState('');
  const [targetBucket, setTargetBucket] = useState(null);
  const [groupSelectionSnapshot, setGroupSelectionSnapshot] = useState([]);
  const [groupResult, setGroupResult] = useState(null);
  const [activeSuggestCombo, setActiveSuggestCombo] = useState(null);

  // Step "Cosa vuoi ottenere?": slider fantamedia⇄copertura + preferenze +
  // ruolo da rinforzare.
  const [coverageWeight, setCoverageWeight] = useState(40); // 0 = solo fantamedia, 100 = solo copertura
  const [wantSoftCalendar, setWantSoftCalendar] = useState(false);
  const [wantContinuity, setWantContinuity] = useState(false);

  useEffect(() => {
    setGroupResult(null);
    setActiveSuggestCombo(null);
  }, [targetTeam]);

  const selectedMyPurchasesLive = useMemo(
    () => myRosterPurchases.filter((p) => selectedMyIds.has(String(p.playerId))),
    [myRosterPurchases, selectedMyIds]
  );

  const distinctBuckets = useMemo(
    () => getDistinctBuckets(selectedMyPurchasesLive, rolesMode),
    [selectedMyPurchasesLive, rolesMode]
  );

  // Ruolo da rinforzare: sempre uno tra i ruoli che stai offrendo (il pacchetto
  // ricevuto rispecchia gli stessi ruoli). Default = il primo.
  const [chosenBucket, setChosenBucket] = useState(null);
  useEffect(() => {
    setChosenBucket((cur) => (cur && distinctBuckets.includes(cur) ? cur : (distinctBuckets[0] || null)));
    setGroupResult(null);
  }, [distinctBuckets]);

  // Shortlist: a quali squadre conviene di più proporre, in base ai ruoli che
  // stai offrendo (bisogno + riserve cedibili). Disponibile già allo step 2.
  const teamShortlist = useMemo(
    () => rankTargetTeams({
      selectedPurchases: selectedMyPurchasesLive,
      teamNames, rosterByTeam, myTeamName, rolesMode, auction,
    }),
    [selectedMyPurchasesLive, teamNames, rosterByTeam, myTeamName, rolesMode, auction]
  );
  const shortlistByTeam = useMemo(() => {
    const m = new Map();
    teamShortlist.forEach((s) => m.set(s.team, s));
    return m;
  }, [teamShortlist]);

  const toggleMySelection = (playerId) => {
    setSelectedMyIds((prev) => {
      const next = new Set(prev);
      const key = String(playerId);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const resultsRef = useRef(null);

  const runGroupSearch = () => {
    if (!selectedMyPurchasesLive.length || !chosenBucket) return;
    const result = buildGroupTradeSuggestions({
      selectedPurchases: selectedMyPurchasesLive,
      targetBucket: chosenBucket,
      teamNames: targetTeam ? [myTeamName, targetTeam] : teamNames,
      rosterByTeam,
      myTeamName,
      rolesMode,
      auction,
      availabilityById,
      ratingsById,
      objective: {
        coverageWeight: coverageWeight / 100,
        softCalendar: wantSoftCalendar,
        continuity: wantContinuity,
      },
    });

    setTargetBucket(chosenBucket);
    setGroupSelectionSnapshot(selectedMyPurchasesLive);
    setGroupResult(result);
    setActiveSuggestCombo(null);

    // Con la lista giocatori/step sopra a volte alta, i pacchetti proposti
    // finivano fuori dallo schermo senza che l'utente se ne accorgesse:
    // porta la vista sui risultati appena vengono generati.
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleReset = () => {
    setSelectedMyIds(new Set());
    setTargetBucket(null);
    setGroupSelectionSnapshot([]);
    setGroupResult(null);
    setActiveSuggestCombo(null);
  };

  // Con una squadra scelta, mostra solo il suo gruppo; con "qualsiasi squadra"
  // mostra tutti i gruppi trovati (già ordinati e limitati dal motore per
  // convenienza/obiettivo, vedi buildGroupTradeSuggestions).
  const groupsToShow = useMemo(() => {
    if (!groupResult) return [];
    if (targetTeam) return groupResult.teamGroups.filter((g) => g.team === targetTeam);
    return groupResult.teamGroups;
  }, [groupResult, targetTeam]);

  return (
    <div className="scambi-wtw">
      <div className="scambi-wtw__header">
        <div>
          <h2>
            {auction.name}
            {isWatched && (
              <span className="scambi-wtw__watchedTag"><Eye size={12} /> Asta seguita</span>
            )}
          </h2>
          <span>{myTeamName} · {teamNames.length} squadre</span>
        </div>
        <button type="button" className="scambi-change-auction-btn" onClick={onChangeAuction}>
          <RotateCcw size={14} />
          Cambia asta
        </button>
      </div>

      <div className="scambi-wtw-flow">
        <section className="scambi-step glass">
          <div className="scambi-step__head">
            <span className="scambi-step__num">1</span>
            <div>
              <strong>Seleziona uno o più giocatori della tua rosa</strong>
              <span>Verranno considerati come un unico pacchetto da offrire.</span>
            </div>
          </div>

          <div className="trade-picker__list trade-picker__list--grouped scambi-roster-scroll">
            <GroupedPlayerPickList
              grouped={myRosterGrouped}
              rolesMode={rolesMode}
              selectedIds={selectedMyIds}
              onToggle={toggleMySelection}
              emptyLabel="Non hai ancora giocatori in rosa."
            />
          </div>
        </section>

        <section className="scambi-step glass">
          <div className="scambi-step__head">
            <span className="scambi-step__num">2</span>
            <div>
              <strong>Con chi vuoi scambiare?</strong>
              <span>
                {targetTeam
                  ? 'I pacchetti proposti verranno calcolati solo per la squadra scelta.'
                  : "L'algoritmo scandaglia tutta la lega e ti mostra le proposte migliori, squadra per squadra."}
              </span>
            </div>
          </div>

          <div className="scambi-scope-toggle">
            <button
              type="button"
              className={`scambi-scope-toggle__opt${!targetTeam ? ' is-active' : ''}`}
              onClick={() => setTargetTeam('')}
            >
              <span className="scambi-scope-toggle__icon"><Radar size={18} /></span>
              <span className="scambi-scope-toggle__text">
                <strong>Tutta la lega</strong>
                <em>Radar sulle squadre più convenienti</em>
              </span>
            </button>
            <button
              type="button"
              className={`scambi-scope-toggle__opt${targetTeam ? ' is-active' : ''}`}
              onClick={() => setTargetTeam((cur) => cur || teamShortlist[0]?.team || otherTeamNames[0] || '')}
            >
              <span className="scambi-scope-toggle__icon"><Target size={18} /></span>
              <span className="scambi-scope-toggle__text">
                <strong>Una squadra precisa</strong>
                <em>Scegli tu con chi trattare</em>
              </span>
            </button>
          </div>

          {teamShortlist.length > 0 && (
            <div className="scambi-shortlist">
              <span className="scambi-shortlist__label">
                {targetTeam ? 'Consigliate per ciò che offri' : 'Più ricettive: tocca per restringere la ricerca'}
              </span>
              <div className="scambi-shortlist__items">
                {teamShortlist.map((s) => {
                  const shortReason = s.hardGap > 0
                    ? 'rischia di giocare in 10'
                    : s.needBuckets.length
                      ? `corta in ${s.needBuckets.map((b) => ROLE_LABELS[b] || b).join(', ')}`
                      : 'ha riserve da darti';
                  return (
                    <button
                      key={s.team}
                      type="button"
                      className={`scambi-shortlist__item scambi-shortlist__item--${s.tier}${targetTeam === s.team ? ' is-active' : ''}`}
                      onClick={() => setTargetTeam(s.team)}
                    >
                      {s.tier === 'best' && <Star size={12} />}
                      <span className="scambi-shortlist__team">{s.team}</span>
                      <span className="scambi-shortlist__reason">
                        {shortReason}
                        {s.canGiveBack && s.needBuckets.length ? ' · può ricambiare' : ''}
                      </span>
                      {s.weaknessReasons?.length > 0 && (
                        <AppTooltip
                          as="span"
                          className="scambi-shortlist__info"
                          content={s.weaknessReasons.join(' · ')}
                        >
                          <Info size={12} />
                        </AppTooltip>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {targetTeam && (
            <div className="scambi-team-pills">
              {otherTeamNames.map((team) => {
                const s = shortlistByTeam.get(team);
                return (
                  <button
                    key={team}
                    type="button"
                    className={`scambi-team-pill${targetTeam === team ? ' is-active' : ''}${s ? ` scambi-team-pill--${s.tier}` : ''}`}
                    onClick={() => setTargetTeam(team)}
                  >
                    {s?.tier === 'best' && <Star size={11} />}
                    {team}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="scambi-step glass">
          <div className="scambi-step__head">
            <span className="scambi-step__num">3</span>
            <div>
              <strong>Cosa vuoi ottenere dallo scambio?</strong>
              <span>L'algoritmo cerca i pacchetti giusti per il tuo obiettivo, non il più conveniente in assoluto.</span>
            </div>
          </div>

          <div className="scambi-objective">
            <div className="obj-block">
              <span className="obj-block__label">Priorità</span>
              <div className="obj-priority">
                <div className={`obj-priority__pole${coverageWeight < 50 ? ' is-lead' : ''}`}>
                  <TrendingUp size={16} />
                  <span>Fantamedia dell'11</span>
                </div>
                <div className={`obj-priority__pole obj-priority__pole--right${coverageWeight > 50 ? ' is-lead' : ''}`}>
                  <ShieldCheck size={16} />
                  <span>Copertura reparti</span>
                </div>
              </div>
              <input
                className="obj-priority__slider"
                type="range"
                min="0"
                max="100"
                step="10"
                value={coverageWeight}
                onChange={(e) => setCoverageWeight(Number(e.target.value))}
                aria-label="Priorità: fantamedia contro copertura reparti"
              />
              <div className="obj-priority__readout">
                <Scale size={13} />
                <span>
                  {coverageWeight <= 20 && 'Conta solo la qualità dell’undici titolare.'}
                  {coverageWeight > 20 && coverageWeight < 45 && 'Soprattutto fantamedia, senza scoprirti troppo.'}
                  {coverageWeight >= 45 && coverageWeight <= 55 && 'Fantamedia e copertura pesano uguale.'}
                  {coverageWeight > 55 && coverageWeight < 80 && 'Soprattutto non restare corto in un reparto.'}
                  {coverageWeight >= 80 && 'Conta solo non rischiare di giocare in 10, anche a costo di fantamedia.'}
                </span>
              </div>
            </div>

            <div className="obj-block">
              <span className="obj-block__label">Ruolo da rinforzare</span>
              <div className="obj-role__chips">
                {distinctBuckets.map((bucket) => (
                  <button
                    key={bucket}
                    type="button"
                    className={`scambi-role-chip${chosenBucket === bucket ? ' is-active' : ''}`}
                    onClick={() => setChosenBucket(bucket)}
                  >
                    <RoleChip role={bucket} />
                    {ROLE_LABELS[bucket] || bucket}
                  </button>
                ))}
              </div>
              {distinctBuckets.length > 1 && (
                <span className="obj-block__hint">
                  Metti in preventivo di indebolirti negli altri ruoli pur di rinforzare questo.
                </span>
              )}
            </div>

            <div className="obj-block">
              <span className="obj-block__label">Preferenze <em>· facoltative</em></span>
              <div className="obj-prefs">
                <button
                  type="button"
                  className={`obj-pref${wantSoftCalendar ? ' is-active' : ''}`}
                  aria-pressed={wantSoftCalendar}
                  onClick={() => setWantSoftCalendar((v) => !v)}
                >
                  <CalendarDays size={15} />
                  <span>Calendario più morbido nelle prossime giornate</span>
                </button>
                <button
                  type="button"
                  className={`obj-pref${wantContinuity ? ' is-active' : ''}`}
                  aria-pressed={wantContinuity}
                  onClick={() => setWantContinuity((v) => !v)}
                >
                  <Activity size={15} />
                  <span>Chi gioca di più (più presenze / titolarità)</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="scambi-step__actions">
          <button
            type="button"
            className="confirm-btn"
            disabled={!selectedMyIds.size || !chosenBucket}
            onClick={runGroupSearch}
          >
            <Repeat size={16} />
            Trova possibili scambi
          </button>

          <button
            type="button"
            className="reset-btn"
            disabled={!selectedMyIds.size && !groupResult}
            onClick={handleReset}
          >
            <RotateCcw size={15} />
            Reset scambi
          </button>
        </div>

        <div className="scambi-results" ref={resultsRef}>
          {groupResult && (
            <div className="group-results-head">
              <span>Ruoli coinvolti:</span>
              {(groupResult.requiredBuckets || []).map((bucket) => (
                <RoleChip key={bucket} role={bucket} />
              ))}
              {targetBucket && (
                <span>Priorità rinforzo: <strong>{ROLE_LABELS[targetBucket]}</strong></span>
              )}
              <span>
                Obiettivo:{' '}
                <strong>
                  {(groupResult.objective?.coverageWeight ?? 0) >= 0.55
                    ? 'copertura reparti'
                    : (groupResult.objective?.coverageWeight ?? 0) <= 0.2
                      ? 'fantamedia 11'
                      : 'fantamedia + copertura'}
                </strong>
              </span>
              {targetBucket && (freeAgentsByRole[targetBucket]?.length ?? 0) <= 2 && (
                <span className="group-results-head__scarce">
                  <ShieldAlert size={12} />
                  Ruolo scarso sul mercato ({freeAgentsByRole[targetBucket]?.length || 0} svincolati liberi)
                </span>
              )}
            </div>
          )}

          {!groupResult && (
            <div className="trade-target-group__empty trade-target-group__empty--placeholder">
              Seleziona i tuoi giocatori, scegli con chi scambiare e premi "Trova possibili scambi". I pacchetti proposti
              mantengono sempre gli stessi ruoli che offri e vengono filtrati perché convengano anche all'altra squadra:
              così hai più probabilità che lo scambio venga accettato.
            </div>
          )}

          {groupResult && !groupsToShow.length && (
            <div className="trade-target-group__empty">
              {targetTeam
                ? `Nessuno scambio realistico trovato con ${targetTeam} per questa selezione: nessun pacchetto con gli
                   stessi ruoli che ti farebbe guadagnare senza essere sproporzionato. Prova con un altro gruppo di
                   giocatori o un'altra squadra.`
                : 'Nessuno scambio realistico trovato con nessuna squadra della lega per questa selezione: prova con un altro gruppo di giocatori.'}
            </div>
          )}

          {groupsToShow.map((group, groupIdx) => {
            const theirRosterCounts = buildRosterCounts(rosterByTeam[group.team] || [], rolesMode);
            return (
              <div key={group.team} className="scambi-team-group">
                {!targetTeam && (
                  <div className="scambi-team-group__head">
                    <span className="scambi-team-group__rank">#{groupIdx + 1}</span>
                    <strong>{group.team}</strong>
                    <AcceptanceBadge acceptance={group.bestAcceptance} />
                  </div>
                )}
                <div className="scambi-combo-grid">
                  {group.combos.map((combo) => {
                    const comboKey = `${combo.team}-${combo.players.map((p) => p.playerId).join('_')}`;
                    return (
                      <div key={comboKey} className="scambi-combo-cell">
                        <GroupSuggestionCard
                          combo={combo}
                          offeredPlayers={groupSelectionSnapshot}
                          rolesMode={rolesMode}
                          onOpenAnalysis={() => setActiveSuggestCombo({ combo, theirRosterCounts })}
                        />

                        <PublishTradeControl
                          key={comboKey}
                          mySelected={groupSelectionSnapshot}
                          theirSelected={combo.players}
                          myTeamName={myTeamName}
                          theirTeamName={combo.team}
                          rolesMode={rolesMode}
                          auction={auction}
                          myRosterCounts={myRosterCountsForMe}
                          theirRosterCounts={theirRosterCounts}
                          kind="wintowin"
                          meta={{ auctionName: auction.name, myTeamName, theirTeamName: combo.team }}
                          onGoToCommunity={onGoToCommunity}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activeSuggestCombo && (
        <TradeAnalysisOverlay onClose={() => setActiveSuggestCombo(null)}>
          <TradeAnalysisReport
            mySelected={groupSelectionSnapshot}
            theirSelected={activeSuggestCombo.combo.players}
            myTeamName={myTeamName}
            theirTeamName={activeSuggestCombo.combo.team}
            rolesMode={rolesMode}
            auction={auction}
            myRosterCounts={myRosterCountsForMe}
            theirRosterCounts={activeSuggestCombo.theirRosterCounts}
            myRoster={myRosterPurchases}
            theirRoster={rosterByTeam[activeSuggestCombo.combo.team] || []}
            showCredits={false}
          />
        </TradeAnalysisOverlay>
      )}
    </div>
  );
}

export default function ScambiWinToWinTab({ onGoToCommunity }) {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [watchedAuctions, setWatchedAuctions] = useState([]);
  const [watchedHostNames, setWatchedHostNames] = useState({});
  const [loadingAuctions, setLoadingAuctions] = useState(true);
  // { auctionId, ownerUid, isWatched } | null
  const [selection, setSelection] = useState(null);
  // Per le aste seguite (di altri) l'utente sceglie quale squadra è la sua.
  const [myTeamChoice, setMyTeamChoice] = useState('');

  useEffect(() => {
    if (!user?.uid) return;
    setLoadingAuctions(true);
    Promise.all([
      getUserAuctions(user.uid).catch(() => []),
      getUserWatchedAuctions(user.uid).catch(() => []),
    ])
      .then(async ([own, watched]) => {
        setAuctions(own);
        // Evita doppioni: se un'asta seguita è già tra le proprie, tienila tra le proprie.
        const ownIds = new Set(own.map((a) => String(a.id)));
        const followed = (watched || []).filter((a) => !ownIds.has(String(a.id)));
        setWatchedAuctions(followed);
        if (followed.length) {
          const names = await getUserDisplayNamesByIds(
            followed.map((a) => a.watchedOwnerUid)
          ).catch(() => ({}));
          setWatchedHostNames(names);
        }
      })
      .finally(() => setLoadingAuctions(false));
  }, [user?.uid]);

  const rosterData = useAuctionRosterData(selection?.ownerUid, selection?.auctionId);

  const resetSelection = () => {
    setSelection(null);
    setMyTeamChoice('');
  };

  if (!selection) {
    return (
      <div className="scambi-auction-picker">
        <p className="scambi-hint">Scegli quale asta vuoi analizzare per trovare scambi vantaggiosi per entrambi.</p>

        {loadingAuctions ? (
          <div className="scambi-loading"><Loader2 size={20} className="spin" /> Carico le tue aste...</div>
        ) : auctions.length === 0 && watchedAuctions.length === 0 ? (
          <div className="trade-target-group__empty">
            Non hai ancora nessuna asta. Creane una dalla sezione "Aste", o segui un'asta pubblica, per poter analizzare scambi.
          </div>
        ) : (
          <>
            {auctions.length > 0 && (
              <>
                <h3 className="scambi-picker-group-title">Le tue aste</h3>
                <div className="scambi-auction-grid">
                  {auctions.map((auction) => {
                    const auctionRolesMode = getAuctionRolesMode(auction);
                    return (
                      <button
                        key={auction.id}
                        type="button"
                        className="scambi-auction-pick"
                        onClick={() => setSelection({ auctionId: auction.id, ownerUid: user.uid, isWatched: false })}
                      >
                        <span className="scambi-auction-pick__name">{auction.name}</span>
                        <span className={`scambi-auction-pick__roles scambi-auction-pick__roles--${auctionRolesMode}`}>
                          {auctionRolesMode === 'mantra' ? 'Mantra' : 'Classic'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {watchedAuctions.length > 0 && (
              <>
                <h3 className="scambi-picker-group-title">
                  <Eye size={14} /> Aste che segui
                </h3>
                <div className="scambi-auction-grid">
                  {watchedAuctions.map((auction) => {
                    const auctionRolesMode = getAuctionRolesMode(auction);
                    const hostName = watchedHostNames[auction.watchedOwnerUid];
                    return (
                      <button
                        key={auction.id}
                        type="button"
                        className="scambi-auction-pick"
                        onClick={() => setSelection({
                          auctionId: auction.id,
                          ownerUid: auction.watchedOwnerUid,
                          isWatched: true,
                        })}
                      >
                        <span className="scambi-auction-pick__main">
                          <span className="scambi-auction-pick__name">{auction.name}</span>
                          <span className="scambi-auction-pick__host">di {hostName || 'Utente'}</span>
                        </span>
                        <span className={`scambi-auction-pick__roles scambi-auction-pick__roles--${auctionRolesMode}`}>
                          {auctionRolesMode === 'mantra' ? 'Mantra' : 'Classic'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  if (rosterData.loading) {
    return <div className="scambi-loading"><Loader2 size={20} className="spin" /> Carico l'asta selezionata...</div>;
  }

  if (rosterData.notFound || !rosterData.auction) {
    return (
      <div className="trade-target-group__empty">
        Non sono riuscito a caricare questa asta.
        <button type="button" className="scambi-change-auction-btn" onClick={resetSelection}>
          Torna alla scelta dell'asta
        </button>
      </div>
    );
  }

  const auctionTeamNames = rosterData.auction.teamNames || [];

  // Per un'asta seguita (di un altro) va scelta la squadra "propria": il
  // myTeamName dell'asta è quello del PROPRIETARIO, non del follower.
  if (selection.isWatched && !myTeamChoice) {
    return (
      <div className="scambi-auction-picker">
        <div className="scambi-wtw__header">
          <div>
            <h2>{rosterData.auction.name}</h2>
            <span>{auctionTeamNames.length} squadre</span>
          </div>
          <button type="button" className="scambi-change-auction-btn" onClick={resetSelection}>
            <RotateCcw size={14} />
            Cambia asta
          </button>
        </div>
        <p className="scambi-hint">Quale squadra è la tua in questa asta? Gli scambi verranno calcolati dal suo punto di vista.</p>
        <div className="scambi-auction-grid">
          {auctionTeamNames.map((team) => (
            <button
              key={team}
              type="button"
              className="scambi-auction-pick"
              onClick={() => setMyTeamChoice(team)}
            >
              <span className="scambi-auction-pick__name">{team}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const myTeamName = selection.isWatched ? myTeamChoice : rosterData.myTeamName;
  const myRosterPurchases = selection.isWatched
    ? (rosterData.rosterByTeam?.[myTeamChoice] || [])
    : (rosterData.myRosterPurchases || []);

  return (
    <ScambiWinToWinAnalysis
      key={`${selection.auctionId}:${myTeamName}`}
      auction={rosterData.auction}
      rolesMode={rosterData.rolesMode}
      teamNames={auctionTeamNames}
      rosterByTeam={rosterData.rosterByTeam}
      myRosterPurchases={myRosterPurchases}
      myTeamName={myTeamName}
      players={rosterData.players}
      purchases={rosterData.purchases}
      isWatched={selection.isWatched}
      onChangeAuction={resetSelection}
      onGoToCommunity={onGoToCommunity}
    />
  );
}
